import {
  useCallback, useMemo,
} from 'react';
import { isResourceLoaded } from '~/_shared/types/fetchableResource/fetchableResource.helpers';
import { isSpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { uniqueValues } from '~/_shared/utils/array/array.helpers';
import { flatten } from '~/_shared/utils/collections/collections';
import { createUuid } from '~/_shared/utils/createUuid';
import { useTranslation } from '~/_shared/utils/hooks';
import { numberObjectKeys } from '~/_shared/utils/object/objectEnumerators';
import { notNull } from '~/_shared/utils/typeGuards';
import type { ChartItem } from '~/charts/chartItem/chartItem.types';
import { useModal } from '~/modal/useModal.hook';
import { useBoundaryStateItem } from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryStateItem';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  useBoundaryGroupsSelector, useSelectAllDemographicsMap,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { areChartMetricsSubsetOf } from '~/store/mapSettings/data/charts/mapSettingsCharts.helpers';
import {
  type Chart, type ChartBoundaryStatistics, isChartBoundaryStatistics,
  isChartMetricDemographic, isChartMetricLocationCount, isChartMetricSpreadsheetColumn,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import { useMapSettingsCharts } from '~/store/mapSettings/data/charts/useMapSettingsCharts';
import { getMatchupDataColumnName } from '~/store/matchupData/matchupData.helpers';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import { generateGroupIdByName } from '~/store/spreadsheetData/grouping/spreadsheetData.grouping.helpers';
import { useSpreadsheetDataLastVersionsPerRealSpreadsheetSelector } from '~/store/spreadsheetData/spreadsheetData.selectors';
import { useChartsStats } from '~/store/stats/hooks/useChartsStats';
import { useDefaultBTGStats } from '~/store/stats/hooks/useDefaultBTGStats';
import { useManageStats } from '~/store/stats/hooks/useManageStats';
import {
  isBoundaryStatItem, isBoundaryStatItemLocationCount, isBoundaryStatItemSpreadsheetColumn, isStatsItemLoaded,
  isStatsItemLoading, isStatsItemSyncing,
} from '~/store/stats/stats.helpers';
import { useHighlightStatsBoundaryGroupSelector } from '~/store/stats/stats.selectors';
import type {
  BoundaryStatsItem, StatItem,
} from '~/store/stats/stats.state';
import { useAddNewChart } from './addNewChart/useAddNewChart';

type ChartWithStats = Readonly<{
  chart: Chart;
  stats: {
    statsItems: ReadonlyArray<StatItem>;
    values: ChartItem['metrics'];
  };
}>;

export const useMapPageChartItems = () => {
  const [t] = useTranslation();
  const matchupData = useMatchupDataSelector();
  const { charts: savedCharts, addOrUpdateChart } = useMapSettingsCharts();
  const demographicsMap = useSelectAllDemographicsMap();
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const { getBoundaryStateItem } = useBoundaryStateItem();
  const versionsPerRealSpreadsheet = useSpreadsheetDataLastVersionsPerRealSpreadsheetSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const highlightStatsBoundaryGroupId = useHighlightStatsBoundaryGroupSelector();
  const { calculateStats } = useManageStats();
  const { onAddNewChart } = useAddNewChart();
  const { removeCharts } = useMapSettingsCharts();
  const { defaultTerritoryStatsCharts, getBTGIdFromDefaultStatsChart } = useDefaultBTGStats();
  const { openModal: openChartEdit } = useModal('ChartsEdit');

  const boundaryGroupsLookup = useMemo(() => (
    boundaryGroups.reduce((acc, group) => {
      acc.set(group.id, group);
      return acc;
    }, new Map<BoundaryGroupId, typeof boundaryGroups[number]>())
  ), [boundaryGroups]);

  const checkIfOutdatedMetricsDataVersions = useCallback((statsItems: ReadonlyArray<StatItem>): boolean => {
    return statsItems.some((statItem) => {
      if (isBoundaryStatItemSpreadsheetColumn(statItem) || isBoundaryStatItemLocationCount(statItem)) {
        if (isResourceLoaded(statItem.data) && statItem.data.value?.extra.spreadsheetVersions) {
          const itemHasNewerSpreadsheetVersion = statItem.data.value.extra.spreadsheetVersions.entries().some(([realSpreadsheetId, statsVersion]) => {
            const lastVersionId = versionsPerRealSpreadsheet.get(realSpreadsheetId);
            if (lastVersionId !== undefined && statsVersion < lastVersionId) {
              return true;
            }
            return false;
          });
          if (itemHasNewerSpreadsheetVersion) {
            return true;
          }
        }
      }
      if (isBoundaryStatItem(statItem)) {
        const boundaryGroupEtag = boundaryGroupsLookup.get(statItem.boundaryGroupId)?.etag;
        if (boundaryGroupEtag && isResourceLoaded(statItem.data)
          && statItem.data.value.extra.boundaryGroupEtag !== boundaryGroupEtag.timestamp
        ) {
          return true;
        }
      }
      return false;
    });
  }, [boundaryGroupsLookup, versionsPerRealSpreadsheet]);

  const allCharts = useMemo(() => [
    ...savedCharts.filter((chart) => (
      !isChartBoundaryStatistics(chart)
        || (boundaryTerritoryGroups.some((btg) => (
          btg.boundaryGroupId === chart.boundaryGroupId
        )) && chart.metrics.length)
    )),
    ...defaultTerritoryStatsCharts.filter((chart) =>
      !savedCharts.some((savedChart) => (
        (
          isChartBoundaryStatistics(savedChart)
          && savedChart.boundaryGroupId === chart.boundaryGroupId
          && areChartMetricsSubsetOf(chart.metrics, savedChart.metrics)
        )
        || (savedChart.id === chart.id) // this means it was saved as a custom chart
      ))
    ),
  ], [boundaryTerritoryGroups, defaultTerritoryStatsCharts, savedCharts]);

  const { chartsStats, groupValueLabelsLookup } = useChartsStats(allCharts);

  const mergeChartBoundaryStatisticsWithStats = useCallback((
    chart: ChartBoundaryStatistics,
    stats: ReadonlyArray<BoundaryStatsItem | null>,
  ): ChartWithStats => {
    const onlyValidStats = stats.filter(notNull);
    const allMetricsBoundaryIdsForChart = uniqueValues(flatten(onlyValidStats.map((statItem) => {
      if (isResourceLoaded(statItem.data) && statItem.data.value.data?.values) {
        return numberObjectKeys(statItem.data.value.data.values);
      }
      return [];
    })));

    const boundaryIdsAndNames = allMetricsBoundaryIdsForChart.map((boundaryId, index) => [
      boundaryId,
      getBoundaryStateItem(boundaryId, chart.boundaryGroupId)?.displayName || `${t('Territory')} #${index + 1}`,
    ] as const);

    const boundaryIdsAndNamesSorted = boundaryIdsAndNames.toSorted(
      (a, b) => a[0] - b[0],
    );

    const metricsNames = onlyValidStats.map((statItem, index) => {
      if (isChartMetricSpreadsheetColumn(statItem)) {
        return getMatchupDataColumnName(matchupData, statItem.column);
      }
      if (isChartMetricLocationCount(statItem)) {
        if (isSpreadsheetColumnId(statItem.column)) {
          const columnName = getMatchupDataColumnName(matchupData, statItem.column);

          let columnValue: string | undefined = undefined;
          if (statItem.columnValue) {
            const groupId = generateGroupIdByName(statItem.columnValue);
            columnValue = groupValueLabelsLookup.get(statItem.column.spreadsheetId)?.get(statItem.column.columnId)?.get(groupId);

            return columnValue ?? statItem.columnValue; // if the value is not in the current we fall back to showing the raw column value
          }

          return `${t('Empty')} | ${columnName}`;
        }
        else {
          return t('locationCount');
        }
      }
      if (isChartMetricDemographic(statItem)) {
        const demographic = demographicsMap.get(statItem.demographicId);
        if (demographic) {
          return demographic.name;
        }
      }
      return `${t('Metric')} #${index + 1}`;
    });

    return {
      chart,
      stats: {
        statsItems: onlyValidStats,
        values: {
          labels: boundaryIdsAndNamesSorted.map((item) => item[1]),
          barValues: onlyValidStats.map((statItem, index) => ({
            name: metricsNames[index] || `${t('Metric')} ${index + 1}`,
            values: isResourceLoaded(statItem.data)
              ? boundaryIdsAndNamesSorted.map((item) => {
                const boundaryId = item[0];
                return statItem.data.value?.data?.values[boundaryId] ?? 0;
              })
              : [],
          })),
          medians: onlyValidStats.map((statItem, index) => ({
            name: `${t('Median')} ${metricsNames[index] || `${t('Metric')} ${index + 1}`}`,
            value: isResourceLoaded(statItem.data) ? statItem.data.value?.data?.median ?? 0 : 0,
          })),
          averages: onlyValidStats.map((statItem, index) => ({
            name: `${t('Average')} ${metricsNames[index] || `${t('Metric')} ${index + 1}`}`,
            value: isResourceLoaded(statItem.data) ? statItem.data.value?.data?.avg ?? 0 : 0,
          })),
        },
      },
    };
  }, [demographicsMap, groupValueLabelsLookup, getBoundaryStateItem, matchupData, t]);

  const allChartsWithStats = useMemo(() => allCharts.map((chart, index) => {
    if (isChartBoundaryStatistics(chart)) {
      return mergeChartBoundaryStatisticsWithStats(chart, chartsStats[index] || []);
    }
    return {
      chart,
      stats: {
        statsItems: [],
        values: {
          averages: [],
          barValues: [],
          labels: [],
          medians: [],
        },
      },
    };
  }), [allCharts, chartsStats, mergeChartBoundaryStatisticsWithStats]);

  const chartItems = useMemo(() => allChartsWithStats.map((chartWithStats, index) => {
    const isSyncing = chartWithStats.stats.statsItems.some(statItem => isStatsItemSyncing(statItem));
    const isLoading = chartWithStats.stats.statsItems.some(statItem => isStatsItemLoading(statItem));
    const hasError = chartWithStats.stats.statsItems.some(statItem => !isStatsItemLoading(statItem) && !isStatsItemLoaded(statItem));
    const hasNoData = chartWithStats.stats.statsItems.every(statItem => !statItem.data || (isResourceLoaded(statItem.data) && !statItem.data.value.data));
    const isOutdated = checkIfOutdatedMetricsDataVersions(chartWithStats.stats.statsItems);
    const defaultChartBtgId = getBTGIdFromDefaultStatsChart(chartWithStats.chart.id);
    const isDefaultCreatedChart = !!defaultChartBtgId;

    return {
      id: chartWithStats.chart.id,
      title: chartWithStats.chart.title || `${t('Chart')} #${index + 1}`,
      isSyncing,
      isOutdated,
      isLoading: isLoading || (isSyncing && hasNoData),
      hasError,
      shouldNormalize: chartWithStats.chart.shouldNormalize,
      metrics: {
        averages: chartWithStats.stats.values.averages,
        barValues: chartWithStats.stats.values.barValues,
        labels: chartWithStats.stats.values.labels,
        medians: chartWithStats.stats.values.medians,
      },
      onDeleteClick: isDefaultCreatedChart ? undefined : () => removeCharts([chartWithStats.chart.id]),
      onNormalizeToggleClick: (newValue: boolean) => {
        addOrUpdateChart({
          ...chartWithStats.chart,
          // create new chart on normalize toggle to avoid conflicts with default charts
          id: isDefaultCreatedChart ? createUuid() : chartWithStats.chart.id,
          shouldNormalize: newValue,
        });
      },
      onRefreshClick: isOutdated && !isSyncing ? () => calculateStats(chartWithStats.stats.statsItems) : undefined,
      onEditClick: !isSyncing ? () => {
        openChartEdit({
          chartId: isDefaultCreatedChart ? undefined : chartWithStats.chart.id,
          defaultStatsBoundaryGroupId: isDefaultCreatedChart && isChartBoundaryStatistics(chartWithStats.chart)
            ? chartWithStats.chart.boundaryGroupId : undefined,
        });
      } : undefined,
    } satisfies ChartItem;
  }), [allChartsWithStats, checkIfOutdatedMetricsDataVersions, getBTGIdFromDefaultStatsChart, t, removeCharts, addOrUpdateChart, calculateStats, openChartEdit]);

  const chartIdToScrollTo = useMemo(() => {
    if (!highlightStatsBoundaryGroupId) {
      return undefined;
    }

    const chartWithHighlightedBoundaryGroupIndex = allCharts.findIndex((chart) => (
      isChartBoundaryStatistics(chart)
      && chart.boundaryGroupId === highlightStatsBoundaryGroupId
    ));

    // allCharts and chartItems have the same order, so we can use the index from allCharts
    if (chartWithHighlightedBoundaryGroupIndex !== -1) {
      const chartItem = chartItems[chartWithHighlightedBoundaryGroupIndex];
      if (chartItem && !chartItem.isLoading) {
        return chartItem.id;
      }

    }
    return undefined;
  }, [allCharts, chartItems, highlightStatsBoundaryGroupId]);

  return {
    onAddNewChart,
    chartItems,
    chartIdToScrollTo,
  };
};
